import { debounce, toArray, toNumber } from 'lodash-es'
import { KeyboardEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { MapContext } from '@/feature/map'
import { captureError } from '@/utils/captureError'

import { GEO_POINT_REGEX } from '../utils/geopoint'

export type SearchItem = {
  address: string
  location: { lat: number; lng: number }
}

export type SearchResult = {
  id: string
  address: string
  description: string
} & (
  | {
      type: 'place'
    }
  | {
      type: 'geoPoint'
      location: google.maps.LatLngLiteral
    }
)

const wrapNumber = (num: number, min: number, max: number) => {
  if (num < min) {
    return max - (min - num) + 1
  } else if (num > max) {
    return min + (num - max) - 1
  } else {
    return num
  }
}

export const useAutoCompleteLocationSearch = ({
  map,
  searchInfo,
  searchBoxElement,
  onSearchChanged,
  onSearchUpdated,
  onSearchedResults,
}: {
  map: MapContext | null
  searchInfo?: SearchItem
  searchBoxElement: RefObject<HTMLInputElement | null>
  onSearchChanged?: (search: SearchItem | undefined) => void
  onSearchUpdated?: (searchTerm: string) => void
  onSearchedResults?: (locations: SearchResult[]) => void
}) => {
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>()
  const [geoPoint, setGeoPoint] = useState<google.maps.LatLngLiteral>()

  const [forceCloseResults, setForceCloseResults] = useState<boolean>(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>()

  const autoCompleteToken = useRef<google.maps.places.AutocompleteSessionToken>(null)
  if (!autoCompleteToken.current) {
    autoCompleteToken.current = new window.google.maps.places.AutocompleteSessionToken()
  }

  const requestSearchResults = useCallback(
    debounce(async () => {
      const request: google.maps.places.AutocompleteRequest = {
        input: search,
        sessionToken: autoCompleteToken.current!,
        ...(map && {
          locationBias: map.map.getBounds() ?? undefined,
          origin: map.map.getCenter() ?? undefined,
        }),
      }

      return window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
        .then(({ suggestions }) => {
          setSearchResults(
            suggestions
              .filter((s) => s.placePrediction != null)
              .map((s) => ({
                type: 'place' as const,
                id: s.placePrediction!.placeId,
                address: s.placePrediction!.text.toString(),
                description: s.placePrediction!.text.toString(),
              })),
          )
        })
        .catch(captureError(console.error))
        .finally(() => setIsSearching(false))
    }, 300),
    [search, map],
  )

  const checkForGeoPoint = useCallback(() => {
    const results = GEO_POINT_REGEX.exec(search)

    if (results) {
      const lat = toNumber(results.groups?.lat)
      const lng = toNumber(results.groups?.lng)

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        setGeoPoint({ lat, lng })
      }
    } else {
      setGeoPoint(undefined)
    }
  }, [search])

  const updateSearchResults = useCallback(() => {
    setIsSearching(true)
    checkForGeoPoint()
    if (search.length >= 2) {
      requestSearchResults()?.catch(captureError())
    } else {
      setSearchResults(undefined)
      setIsSearching(false)
      onSearchChanged?.(undefined)
    }
  }, [requestSearchResults, checkForGeoPoint])

  const lastSearch = useRef(search)
  useEffect(() => {
    if (lastSearch.current !== search) {
      lastSearch.current = search
      setHighlightedIndex(undefined)
      updateSearchResults()
    }
  }, [search])

  useEffect(() => {
    if (searchInfo) {
      setSearch(searchInfo?.address ?? '')
    }
  }, [searchInfo?.address])

  const locations = useMemo(() => {
    const locations = toArray(searchResults)

    if (geoPoint) {
      locations.unshift({
        id: 'geoPoint',
        type: 'geoPoint',
        address: `Use as geo point "${geoPoint.lat}, ${geoPoint.lng}"`,
        description: search,
        location: geoPoint,
      })
    }

    return locations
  }, [searchResults, geoPoint])

  const onSearchedResultsRef = useRef(onSearchedResults)
  useEffect(() => {
    onSearchedResultsRef.current = onSearchedResults
  })

  const onSearchUpdatedRef = useRef(onSearchUpdated)
  useEffect(() => {
    onSearchUpdatedRef.current = onSearchUpdated
  })

  useEffect(() => {
    onSearchedResultsRef.current?.(locations)
  }, [locations])

  useEffect(() => {
    onSearchUpdatedRef.current?.(search)
  }, [search])

  const onPlaceSelected = useCallback(
    async (selectedPlace: (typeof locations)[0]) => {
      searchBoxElement.current?.blur()
      setForceCloseResults(true)
      setTimeout(() => setForceCloseResults(false), 300)

      if (selectedPlace.type === 'geoPoint') {
        setTimeout(() => {
          map?.panTo({ position: selectedPlace.location })

          onSearchChanged?.({
            address: selectedPlace.description,
            location: selectedPlace.location,
          })
        }, 0)
      } else if (selectedPlace.type === 'place') {
        const place = new window.google.maps.places.Place({ id: selectedPlace.id })

        try {
          await place.fetchFields({ fields: ['formattedAddress', 'location'] })
          map?.panTo({ position: place.location?.toJSON() })
          setSearch(selectedPlace.description)
          onSearchChanged?.({
            address: place.formattedAddress!,
            location: place.location!.toJSON(),
          })
        } catch (error) {
          captureError(console.error)(error)
        } finally {
          autoCompleteToken.current = new window.google.maps.places.AutocompleteSessionToken()
        }
      } else {
        // @ts-expect-error - This should never happen
        throw new Error(`Unknown search result type "${selectedPlace.type}"`)
      }
    },
    [map, onSearchChanged],
  )

  const resolveSearch = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (locations.length === 0 || e.key === 'Escape') {
        setHighlightedIndex(undefined)
      } else if (e.key === 'Enter') {
        const index = highlightedIndex ?? 0
        const selectedPlace = locations[index]

        if (selectedPlace) {
          onPlaceSelected(selectedPlace)
        }
      } else {
        if (e.key === 'ArrowDown') {
          setHighlightedIndex((prev) => wrapNumber((prev ?? -1) + 1, 0, locations.length - 1))
        } else if (e.key === 'ArrowUp') {
          setHighlightedIndex((prev) =>
            wrapNumber((prev ?? locations.length) - 1, 0, locations.length - 1),
          )
        }
      }
    },
    [highlightedIndex, locations],
  )

  return {
    search,
    isSearching,
    searchResults,
    forceCloseResults,
    highlightedIndex,
    geoPoint,
    locations,
    setSearch,
    resolveSearch,
    onPlaceSelected,
    requestSearchResults,
  }
}
